// Display label for a trade. Looks up `trades.<tradeId>` in the active
// react-i18next locale; when no translation exists, falls back to a
// humanised form of the tradeId itself.
//
// Why `en.json` only has a handful of `trades.*` entries while `ja.json`
// has dozens:
//   - Trusstor's CSV (`trusstor_dashboard/shared/translations/translations.json`)
//     happens to be very close to humanised camelCase for English. For
//     ~40 of ~44 trades, `humanizeCamelCase("plotDevelopment")` produces
//     the same string trusstor's CSV does ("Plot Development"). So we
//     skip the en entries and rely on the fallback.
//   - The four entries in `en.json` are the trusstor renames where
//     humanise diverges: `hvac` → "HVAC" (acronym), `guest` → "Visitor",
//     `generalWorks` → "Hourly Works", `siteEngineer` → "Field Engineer".
//   - For Japanese, every entry is load-bearing — humanise emits English
//     regardless of locale, so `ja.json` carries the full mirror of
//     trusstor's `jpn` translations.

import type { TFunction } from 'i18next';

/**
 * "insulation"      → "Insulation"
 * "plotDevelopment" → "Plot Development"
 * "elec-001"        → "Elec 001"
 * "site_engineer"   → "Site Engineer"
 * Best-effort fallback for tradeIds that don't have an explicit i18n
 * entry under `trades.*`.
 */
export function humanizeCamelCase(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

export function tradeLabel(t: TFunction, tradeId: string | undefined): string {
  if (!tradeId) return '';
  return t(`trades.${tradeId}`, { defaultValue: humanizeCamelCase(tradeId) });
}

/** A profession as the issue pickers name an option: its trade, then whose
 *  (contractor, else specialty). Not the pill's label (`Trade, specialty`). */
export function professionOptionLabel(
  t: TFunction,
  profession: { tradeId?: string; contractor?: string | null; specialty?: string | null },
): string {
  const label = tradeLabel(t, profession.tradeId);
  const sub = profession.contractor ?? profession.specialty ?? '';
  return sub ? `${label} — ${sub}` : label;
}
